import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { FcGoogle } from "react-icons/fc";
import { FaApple } from "react-icons/fa";
import { BRAND } from "@/lib/brand";

export default function SignIn() {
    const { login } = useAuth();
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [busy, setBusy] = useState(false);

    const onSubmit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await login(email, password);
            toast.success("welcome back");
            navigate("/companion");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "sign in failed");
        } finally {
            setBusy(false);
        }
    };

    const googleSignIn = () => {
        const redirect = `${window.location.origin}/auth/callback`;
        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirect)}`;
    };

    const appleSignIn = () => {
        toast("sign in with apple — coming soon", {
            description: "needs an apple developer team id + service id. for now, please use google or email.",
        });
    };

    return (
        <div className="relative z-10 min-h-screen flex items-center justify-center px-6">
            <div className="w-full max-w-md">
                <Link to="/" className="block text-center mb-10 font-heading text-3xl tracking-tight text-ink">{BRAND.handle}</Link>
                <div className="bg-white border border-clayHover rounded-3xl p-10 shadow-soft">
                    <h1 className="font-heading text-3xl tracking-tight text-ink mb-2">welcome back</h1>
                    <p className="text-inkSoft font-body mb-8">it's good to see you again.</p>

                    <button
                        data-testid="google-signin-btn"
                        onClick={googleSignIn}
                        type="button"
                        className="w-full flex items-center justify-center gap-3 border border-clayHover rounded-full py-3 mb-3 hover:bg-clay transition-colors font-body"
                    >
                        <FcGoogle className="w-5 h-5" />
                        continue with google
                    </button>
                    <button
                        data-testid="apple-signin-btn"
                        onClick={appleSignIn}
                        type="button"
                        className="w-full flex items-center justify-center gap-3 border border-clayHover rounded-full py-3 mb-6 hover:bg-clay transition-colors font-body text-ink"
                    >
                        <FaApple className="w-5 h-5" />
                        continue with apple
                    </button>

                    <div className="flex items-center gap-3 my-5">
                        <div className="flex-1 h-px bg-clayHover" />
                        <span className="text-xs uppercase tracking-widest text-inkSoft">or</span>
                        <div className="flex-1 h-px bg-clayHover" />
                    </div>

                    <form onSubmit={onSubmit} className="space-y-5">
                        <div>
                            <label className="block text-xs uppercase tracking-widest text-inkSoft mb-2">email</label>
                            <input
                                data-testid="signin-email"
                                type="email"
                                required
                                value={email}
                                onChange={e => setEmail(e.target.value)}
                                className="w-full bg-transparent border-b-2 border-clayHover focus:border-terracotta outline-none py-2 text-ink font-body"
                            />
                        </div>
                        <div>
                            <label className="block text-xs uppercase tracking-widest text-inkSoft mb-2">password</label>
                            <input
                                data-testid="signin-password"
                                type="password"
                                required
                                value={password}
                                onChange={e => setPassword(e.target.value)}
                                className="w-full bg-transparent border-b-2 border-clayHover focus:border-terracotta outline-none py-2 text-ink font-body"
                            />
                        </div>
                        <button
                            data-testid="signin-submit"
                            disabled={busy}
                            type="submit"
                            className="w-full bg-terracotta text-bone rounded-full py-3 font-body hover:bg-terracottaHover transition-colors disabled:opacity-60"
                        >
                            {busy ? "..." : "sign in"}
                        </button>
                    </form>

                    <p className="text-center mt-6 text-sm text-inkSoft font-body">
                        new here? <Link to="/signup" data-testid="link-signup" className="text-terracotta hover:underline">begin a conversation</Link>
                    </p>
                </div>
            </div>
        </div>
    );
}
